import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Button, Container, Spinner } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { matchApi, markupApi, eventsApi } from "@/api";
import Preloader from "@/components/Elements/Preloader";
import VideoPlayer from "@/components/VideoPlayer";
import Map from "@/components/Map";
import EditCell from "./EditCell";
import EditableCell from "./EditableCell";
import EventsTable from "./EventsTable";
import { convertTime2Frame, frameToTime } from "@/util/functions";
import "./Validation.scss";
import SubEventsCell from "./SubEventsCell";
import { LABELER } from "@/constants/routs.constants";
import { Arrow } from "react-konva";
import Switch from "@/components/Elements/switch";
import SubEventsList from "./SubEventsList";

const columnHelper = createColumnHelper();

const Validation = () => {
  const navigate = useNavigate();
  const { videoId } = useParams();
  const location = useLocation();
  const videoRef = useRef(null);
  const tableRef = useRef(null);
  const newArrowRef = useRef();
  const new2DArrowRef = useRef();
  const newArrow = useRef({
    visible: false,
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 0,
  });
  const new2DArrow = useRef({
    visible: false,
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 0,
  });

  const [events, setEvents] = useState([]);
  const [currentEvents, setCurrentEvents] = useState([]);
  const [realCurrentEvents, setRealCurrentEvents] = useState([]);
  const [fps, setFps] = useState(1);
  const [videoUrl, setVideoUrl] = useState("");
  const [fieldWidth, setFieldWidth] = useState(0);
  const [fieldHeight, setFieldHeight] = useState(0);
  const [videoStartTime, setVideoStartTime] = useState(null);
  const [videoEndTime, setVideoEndTime] = useState(null);
  const [previewEventId, setPreviewEventId] = useState(null);
  const [videoDomHeight, setVideoDomHeight] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [previewEvent, setPreviewEvent] = useState(null);
  const [frameStep, setFrameStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [supertracks, setSupertracks] = useState({});
  const [activeTracks, setActiveTracks] = useState([]);
  const [supertrackUids, setSupertrackUids] = useState([]);
  const [matchSuperTracks, setMatchSuperTracks] = useState([]);
  const [tableEvents, setTableEvents] = useState([]);
  const [subEvents, setSubEvents] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [stageScale, setStageScale] = useState(1);
  const [stageX, setStageX] = useState(0);
  const [stageY, setStageY] = useState(0);
  const [ratio, setRatio] = useState(1);
  const [initialY, setInitialY] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [newEvent, setNewEvent] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [subEventsListData, setSubEventsListData] = useState({
    isOpen: false,
    visibleSubEvents: [],
    onSelectEvent: null,
    position: { x: 10, y: 10 },
    eventType: null,
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "Id",
        filterFn: "idFilterFn",
      },
      {
        accessorKey: "super_track",
        header: "Active Player",
        cell: (props) => {
          return (
            <EditableCell
              {...props}
              fieldName="super_track_uid"
              fieldType="number"
            />
          );
        },
        meta: {
          filterVariant: "select",
          type: "select",
          options: supertrackUids.map((uid) => ({ label: uid, value: uid })),
        },
        filterFn: "activePlayerFilterFn",
      },
      {
        accessorKey: "extra",
        header: "Passive Player",
        cell: (props) => {
          return (
            <EditableCell {...props} fieldName="player" fieldType="number" />
          );
        },
        meta: {
          filterVariant: "select",
          type: "select",
          options: [
            { label: "None", value: null },
            ...supertrackUids.map((uid) => ({ label: uid, value: uid })),
          ],
        },
        filterFn: "passivePlayerFilterFn",
      },
      {
        accessorKey: "event_type",
        header: "Type",
        cell: EditableCell,
        meta: {
          filterVariant: "select",
          type: "select",
          options: [
            {
              label: "_Pass",
              value: "pass",
            },
            {
              label: "_Shot",
              value: "shot",
            },
            {
              label: "_Tackle",
              value: "tackle",
            },
            {
              label: "_Dribble",
              value: "dribble",
            },
            {
              label: "_Duel",
              value: "duel",
            },
            {
              label: "_Foul",
              value: "foul",
            },
            {
              label: "_Ball Control",
              value: "ball_control",
            },
            {
              label: "_Entries in penalty area",
              value: "entries_in_penalty_area",
            },
            {
              label: "_Sprint",
              value: "sprint",
            },
            {
              label: "_Auto goal",
              value: "auto_goal",
            },
            {
              label: "_Goalkeeper Exit",
              value: "goalkeeper_exit",
            },
          ],
        },
      },
      {
        accessorKey: "success",
        header: "Result",
        cell: EditableCell,
        meta: {
          type: "select",
          options: [
            { label: "_Success", value: true },
            { label: "_Fail", value: false },
          ],
          filterVariant: "select",
        },
        filterFn: "resultFilterFn",
      },
      columnHelper.accessor((row) => row.super_track.kind, {
        id: "kind",
        cell: (props) => (
          <>
            {props.getValue() === "right_team"
              ? "Right"
              : props.getValue() === "left_team"
              ? "Left"
              : "Unknown"}
          </>
        ),
        header: "Team",
        meta: {
          type: "select",
          filterVariant: "select",
          options: [
            { label: "_Right", value: "right_team" },
            { label: "_Left", value: "left_team" },
          ],
        },
        filterFn: "kindFilterFn",
      }),
      columnHelper.accessor((row) => row.extra.additional_events, {
        id: "additional_events",
        cell: (props) => (
          <SubEventsCell {...props} fieldName="additional_events" />
        ),
        header: "Additional Events",
        meta: {
          subEvents: subEvents,
        },
        filterFn: "subEventsFilterFn",
      }),

      // columnHelper.accessor((row) => row.extra.coords_start?.[0] ?? 0, {
      //   id: "X1",
      //   cell: (props) => (
      //     <EditableCell {...props} inputClass="tw-w-16 tw-min-w-16" />
      //   ),
      //   header: "X1",
      //   meta: {
      //     type: "number",
      //     filterVariant: "range",
      //   },
      // }),
      // columnHelper.accessor((row) => row.extra.coords_start?.[1] ?? 0, {
      //   id: "Y1",
      //   cell: (props) => (
      //     <EditableCell {...props} inputClass="tw-w-16 tw-min-w-16" />
      //   ),
      //   header: "Y1",
      //   meta: {
      //     type: "number",
      //     filterVariant: "range",
      //   },
      // }),
      // columnHelper.accessor((row) => row.extra.coords_finish?.[0] ?? 0, {
      //   id: "X2",
      //   cell: (props) => (
      //     <EditableCell {...props} inputClass="tw-w-16 tw-min-w-16" />
      //   ),
      //   header: "X2",
      //   meta: {
      //     type: "number",
      //     filterVariant: "range",
      //   },
      // }),
      // columnHelper.accessor((row) => row.extra.coords_finish?.[1] ?? 0, {
      //   id: "Y2",
      //   cell: (props) => (
      //     <EditableCell {...props} inputClass="tw-w-16 tw-min-w-16" />
      //   ),
      //   header: "Y2",
      //   meta: {
      //     type: "number",
      //     filterVariant: "range",
      //   },
      // }),

      {
        accessorKey: "extra",
        id: "Start Time",
        header: "Start Time",
        cell: (props) => {
          return (
            <EditableCell
              {...props}
              fieldName="current_frame"
              converter={(value) => frameToTime(value, fps)}
            />
          );
        },
        meta: {
          type: "text",
        },
        filterFn: "startTimeFilterFn",
      },
      {
        accessorKey: "extra",
        id: "End Time",
        header: "End Time",
        cell: (props) => {
          return <EditableCell {...props} fieldName="event_end" />;
        },
        meta: {
          type: "text",
        },
        filterFn: "endTimeFilterFn",
      },
      {
        id: "Actions",
        header: "Actions",
        cell: EditCell,
        meta: {
          setSubEventsListData: setSubEventsListData,
        },
      },
    ],
    [supertrackUids, subEvents, fps, setSubEventsListData]
  );

  const deleteEvent = useCallback(
    async (eventId) => {
      try {
        if (eventId === isAdding) {
          setEvents((prevEvents) => [
            ...prevEvents.filter(({ id }) => id !== isAdding),
          ]);
          return;
        }
        const { id, event_type } = events.find(({ id }) => id === eventId);
        const result = window.confirm(
          `Are you sure you want delete ${event_type} event?`
        );

        if (result) {
          await markupApi.deleteEvent(id);
          setEvents((prevEvents) => [
            ...prevEvents.filter(({ id: eventId }) => eventId !== id),
          ]);
          setCurrentEvents((prevEvents) => [
            ...prevEvents.filter(({ id: eventId }) => eventId !== id),
          ]);
          setRealCurrentEvents((prevEvents) => [
            ...prevEvents.filter(({ id: eventId }) => eventId !== id),
          ]);
          toast.success(`Successfully deleted the event ${id}`);
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          "Something went wrong.";
        toast.error(errorMessage);
      }
    },
    [events, isAdding]
  );

  const updateData = useCallback(
    (rowIndex, columnId, eventId, fieldName, value) => {
      let targetEvent;
      if (isAdding) targetEvent = { ...newEvent };
      else targetEvent = events.find(({ id }) => id === eventId);

      if (!targetEvent) {
        console.log("No Target Event");
        return;
      }
      switch (columnId) {
        case "super_track": {
          const st = matchSuperTracks[value];
          if (!st) break;

          const kind = matchSuperTracks[value]?.kind;
          targetEvent.super_track = {
            ...targetEvent.super_track,
            id: st.id,
            super_track_uid: value,
            kind,
          };
          break;
        }
        case "extra": {
          if (value === null) {
            targetEvent.extra = {
              ...targetEvent.extra,
              player: value,
            };
            break;
          }
          const st = matchSuperTracks[value];
          if (!st) break;
          targetEvent.extra = {
            ...targetEvent.extra,
            player: value,
          };
          break;
        }
        case "success":
          targetEvent.success = value === "true" ? true : false;
          break;
        case "Start Time": {
          targetEvent.timestamp = value;
          targetEvent.extra.current_frame = convertTime2Frame(value, fps);
          const newFrame = targetEvent.extra.current_frame - fps * 3;
          const newVideoStart = frameToTime(newFrame >= 0 ? newFrame : 0, fps);
          targetEvent.extra.video_start = newVideoStart;
          break;
        }
        case "End Time": {
          targetEvent.extra.event_end = value;
          const newFrame = convertTime2Frame(value, fps) + fps * 2;
          const newVideoEnd = frameToTime(newFrame, fps);
          targetEvent.extra.video_end = newVideoEnd;
          break;
        }
        case "X1":
          if (!targetEvent.extra.coords_start)
            targetEvent.extra.coords_start = [0, 0];
          targetEvent.extra.coords_start[0] = Number(value);
          break;
        case "Y1":
          if (!targetEvent.extra.coords_start)
            targetEvent.extra.coords_start = [0, 0];
          targetEvent.extra.coords_start[1] = Number(value);
          break;
        case "X2":
          if (!targetEvent.extra.coords_finish)
            targetEvent.extra.coords_finish = [0, 0];
          targetEvent.extra.coords_finish[0] = Number(value);
          break;
        case "Y2":
          if (!targetEvent.extra.coords_finish)
            targetEvent.extra.coords_finish = [0, 0];
          targetEvent.extra.coords_finish[1] = Number(value);
          break;
        case "additional_events":
          targetEvent.extra.additional_events = value;
          break;
        case "event_type":
          targetEvent.event_type = value;
          targetEvent.extra.additional_events = {};
          break;
        default:
          targetEvent[columnId] = value;
      }
      if (isAdding) setNewEvent(targetEvent);
      else setEvents([...events]);
    },
    [events, matchSuperTracks, isAdding, newEvent]
  );

  const updateEvent = useCallback(
    async (eventId) => {
      try {
        if (isAdding) {
          const {
            id,
            highlighted,
            event_length,
            super_track: { super_track_uid, kind },
            ...data
          } = newEvent;

          const { super_track_id, ...createdEvent } = await markupApi.addEvent(
            videoId,
            super_track_uid,
            {
              ...data,
            }
          );

          const sortedEvents = [
            {
              ...createdEvent,
              super_track: {
                super_track_uid,
                kind,
                id: super_track_id,
              },
            },
            ...events,
          ].sort((a, b) => a?.extra.current_frame - b?.extra.current_frame);
          setEvents(sortedEvents);

          setNewEvent(null);
          setIsAdding(false);
          toast.success("Successfully added new event");
        } else {
          const {
            id,
            highlighted,
            event_length,
            super_track: { id: supertrackId },
            ...data
          } = events.find(({ id }) => id === eventId);

          await markupApi.updateEvent(id, {
            ...data,
            super_track: supertrackId,
          });
          toast.success(`Successfully updated the event ${id}`);
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          "Something went wrong while updating event.";
        toast.error(errorMessage);
      }
    },
    [events, isAdding, newEvent]
  );

  useEffect(() => {
    if (isAdding) {
      const tableEvents = showAll
        ? [...events]
        : previewEvent
        ? [previewEvent]
        : [...currentEvents];
      if (newEvent) tableEvents.unshift(newEvent);
      setTableEvents(tableEvents);
      return;
    }
    if (editing && !showAll) return;

    const tableEvents = showAll
      ? [...events]
      : previewEvent
      ? [previewEvent]
      : [...currentEvents];
    if (newEvent) tableEvents.unshift(newEvent);
    setTableEvents(tableEvents);
  }, [showAll, previewEvent, events, currentEvents, newEvent]);

  useEffect(() => {
    async function fetchEvents() {
      try {
        setIsLoading(true);
        let data = await matchApi.getMatch(videoId);
        setFps(data.video.fps);
        setVideoUrl(data.video.origin);

        let events = await matchApi.getEvents({ matchId: videoId });
        events = events.sort(
          (a, b) => a?.extra.current_frame - b?.extra.current_frame
        );
        setEvents(events);
        const {
          field: { width, height },
        } = await markupApi.getFieldMeta(videoId);
        setFieldWidth(width);
        setFieldHeight(height);

        const { frame_step, supertracks, tracks } =
          await matchApi.getMatchSuperTracksFull(videoId);

        if (!supertracks || !tracks) return;

        setMatchSuperTracks(supertracks);

        const newTracks = {};
        const uids = [];
        for (const supertrack_uid in supertracks) {
          uids.push(supertrack_uid);
          for (const track of supertracks[supertrack_uid].tracks) {
            const { track_id } = track;
            newTracks[supertrack_uid] = {
              ...newTracks[supertrack_uid],
              ...tracks[track_id],
            };
          }
        }
        setSupertracks(newTracks);
        setSupertrackUids(uids);

        if (frame_step) setFrameStep(frame_step);

        // Get Task Detail
        // const { task } = await matchApi.postMatchTask(videoId, "validate");
        // if (!task) return;
        // const { frame_start_id, frame_stop_id } = task;

        const query = new URLSearchParams(location.search);
        const taskId = query.get("taskId");
        const frame_start_id = query.get("frame_start_id");
        const frame_stop_id = query.get("frame_stop_id");
        if (frame_start_id !== null && frame_stop_id !== null) {
          setVideoStartTime(frame_start_id / data.video.fps);
          setVideoEndTime(frame_stop_id / data.video.fps);
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          "Something went wrong.";
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
    async function fetchSubEvents() {
      const subEvents = await eventsApi.getSubEvents();
      setSubEvents(subEvents);
    }
    fetchEvents();
    fetchSubEvents();
  }, []);

  useEffect(() => {
    if (!fps) return;

    if (previewEventId === null) {
      const query = new URLSearchParams(location.search);
      const frame_start_id = query.get("frame_start_id");
      const frame_stop_id = query.get("frame_stop_id");
      if (frame_start_id !== null && frame_stop_id !== null) {
        setVideoStartTime(frame_start_id / fps);
        setVideoEndTime(frame_stop_id / fps);
      } else {
        setVideoStartTime(null);
        setVideoEndTime(null);
      }
      setPreviewEvent(null);
    } else {
      const targetEvent = events.find(({ id }) => previewEventId === id);
      if (!targetEvent) return;
      setPreviewEvent(targetEvent);

      const startTime = convertTime2Frame(targetEvent.extra.video_start, fps);
      const endTime = convertTime2Frame(targetEvent.extra.video_end, fps);
      setVideoStartTime(startTime / fps);
      setVideoEndTime(endTime / fps);
    }
  }, [previewEventId, fps]);

  // Function to scroll to bottom
  // const scrollToBottom = () => {
  //   if (tableRef.current) {
  //     tableRef.current.scrollTop = tableRef.current.scrollHeight;
  //   }
  // };

  useEffect(() => {
    const events = previewEvent ? [previewEvent] : realCurrentEvents;
    let newTracks = [];
    for (const event of events) {
      if (!event.super_track) continue;
      const { super_track_uid, tracks_data } = event.super_track;
      newTracks = [...newTracks, String(super_track_uid)];
    }
    setActiveTracks(newTracks);
  }, [realCurrentEvents, previewEvent]);

  const onFrameChange = useCallback(
    (currentFrame) => {
      // const newCurrentEvents = [];
      // const newRealCurrentEvents = [];
      // for (let i = 0; i < currentEvents.length; i++) {
      //   const event = currentEvents[i];
      //   const {
      //     timestamp,
      //     extra: { event_end, video_start, video_end },
      //   } = event;
      //   const startFrameNumber = convertTime2Frame(timestamp, fps);
      //   const endFrameNumber = convertTime2Frame(event_end, fps);
      //   const videoStartFrameNumber = convertTime2Frame(video_start, fps);
      //   const videoEndFrameNumber = convertTime2Frame(video_end, fps);
      //   if (
      //     currentFrame >= videoStartFrameNumber &&
      //     currentFrame <= videoEndFrameNumber
      //   ) {
      //     // This means all upcoming events are valid because events are all sorted.
      //     if (
      //       currentFrame >= startFrameNumber &&
      //       currentFrame <= endFrameNumber
      //     ) {
      //       event.highlighted = true;
      //       newRealCurrentEvents.push(event);
      //     } else {
      //       event.highlighted = false;
      //     }
      //     newCurrentEvents.push(event);
      //   }
      // }

      // let currentIndex =
      //   newCurrentEvents.length === 0
      //     ? 0
      //     : events.findIndex(({ id }) => id === newCurrentEvents.at(-1)?.id);

      // if (currentIndex === undefined) return;
      // if (currentIndex !== 0) currentIndex++;

      // for (; currentIndex < events.length; currentIndex++) {
      //   const event = events[currentIndex];
      //   const {
      //     timestamp,
      //     extra: { event_end, video_start, video_end },
      //   } = event;
      //   const videoStartFrameNumber = convertTime2Frame(video_start, fps);
      //   const videoEndFrameNumber = convertTime2Frame(video_end, fps);
      //   const startFrameNumber = convertTime2Frame(timestamp, fps);
      //   const endFrameNumber = convertTime2Frame(event_end, fps);

      //   if (currentFrame < videoStartFrameNumber) {
      //     break;
      //   }

      //   if (
      //     currentFrame >= videoStartFrameNumber &&
      //     currentFrame <= videoEndFrameNumber
      //   ) {
      //     if (
      //       currentFrame >= startFrameNumber &&
      //       currentFrame <= endFrameNumber
      //     ) {
      //       event.highlighted = true;
      //       newRealCurrentEvents.push(event);
      //     } else {
      //       event.highlighted = false;
      //     }
      //     newCurrentEvents.push(event);
      //   }
      // }

      // setCurrentEvents(newCurrentEvents);
      // setRealCurrentEvents(newRealCurrentEvents);

      const events1 = [],
        events2 = [];
      for (let i = 0; i < events.length; i++) {
        const event = events[i];
        const {
          timestamp,
          extra: { event_end, video_start, video_end },
        } = event;
        const startFrameNumber = convertTime2Frame(timestamp, fps);
        const endFrameNumber = convertTime2Frame(event_end, fps);
        const videoStartFrameNumber = convertTime2Frame(video_start, fps);
        const videoEndFrameNumber = convertTime2Frame(video_end, fps);

        if (
          currentFrame >= videoStartFrameNumber &&
          currentFrame <= videoEndFrameNumber
        ) {
          if (
            currentFrame >= startFrameNumber &&
            currentFrame <= endFrameNumber
          ) {
            event.highlighted = true;
            events2.push(event);
          } else {
            event.highlighted = false;
          }
          events1.push(event);
        }
      }
      setCurrentEvents([...events1]);
      // if (!showAll) scrollToBottom();
      setRealCurrentEvents([...events2]);
    },
    [events, fps, showAll]
  );

  const onVideoDomSizeChange = useCallback((width, height) => {
    setVideoDomHeight(height);
  }, []);

  const turnOffPreviewMode = () => {
    setPreviewEventId(null);
  };

  const saveAnswer = async () => {
    const result = window.confirm(
      `Are you sure you want to complete validation of this match?`
    );

    if (result) {
      try {
        setIsSaving(true);
        const query = new URLSearchParams(location.search);
        const taskId = query.get("taskId");
        if (!taskId) {
          toast.warning("taskId is not specificed.");
          return;
        }

        await matchApi.postMatchTaskResult({
          matchId: videoId,
          taskId: taskId,
          taskResult: {
            status: true,
          },
          taskType: "validate",
        });
        toast.success("Saved the answer successfully.");
        navigate(LABELER);
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          "Something went wrong while saving the answer.";
        toast.error(errorMessage);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const addNewEvent = () => {
    const currentTime = Date.now();

    const startTimeFrame = Math.round(startTime * fps);
    const startTimeStamp = frameToTime(startTimeFrame, fps);
    const videoStartTimeStamp = frameToTime(
      Math.round(startTime * fps - 3 * fps),
      fps
    );
    const endTimeStamp = frameToTime(Math.round(endTime * fps), fps);
    const videoEndTimeStamp = frameToTime(
      Math.round(endTime * fps + 2 * fps),
      fps
    );

    setNewEvent({
      id: currentTime,
      event_type: "pass",
      extra: {
        zoom: {
          x1: 0,
          x2: fieldWidth,
          y1: 0,
          y2: fieldHeight,
        },
        additional_events: {},
        coords_start: [0, 0],
        coords_finish: [0, 0],
        current_frame: startTimeFrame,
        event_end: endTimeStamp,
        player: null,
        video_end: videoEndTimeStamp,
        video_start: videoStartTimeStamp,
      },
      success: true,
      super_track: {
        super_track_uid: supertrackUids[0],
        kind: "right_team",
      },
      timestamp: startTimeStamp,
    });
    setEditing(currentTime);
    setIsAdding(currentTime);
    setStartTime(null);
    setEndTime(null);
  };

  const onStageScaleChange = (newStageScale) => {
    setStageScale(newStageScale);
  };

  const onStagePosChange = (newStageX, newStageY) => {
    setStageX(newStageX);
    setStageY(newStageY);
  };

  // this is called when drawing new rect
  const updateArrowRect = () => {
    const node = newArrowRef.current;
    node.setAttrs({
      visible: newArrow.current.visible,
      points: [
        (newArrow.current.x1 - stageX) / stageScale,
        (newArrow.current.y1 - stageY) / stageScale,
        (newArrow.current.x2 - stageX) / stageScale,
        (newArrow.current.y2 - stageY) / stageScale,
      ],
      stroke: "blue",
      fill: "blue",
      strokeWidth: 1 / stageScale,
      opacity: 0.6,
    });
    node.getLayer().batchDraw();
  };

  const update2DArrowRect = () => {
    const node = new2DArrowRef.current;
    node.setAttrs({
      visible: new2DArrow.current.visible,
      points: [
        (new2DArrow.current.x1 - stageX) / stageScale,
        (new2DArrow.current.y1 - stageY) / stageScale,
        (new2DArrow.current.x2 - stageX) / stageScale,
        (new2DArrow.current.y2 - stageY) / stageScale,
      ],
      stroke: "blue",
      fill: "blue",
      strokeWidth: 1 / stageScale,
      opacity: 0.6,
    });
    node.getLayer().batchDraw();
  };

  const on2DMapMouseDownHandler = (e) => {
    if (!drawing) return;
    if (e.evt.button === 0 && e.evt.ctrlKey) return; // dragging with Ctrl+LMB
    if (e.evt.button === 2) return; // RMB

    setIsDrawing(true);
    // start drawing arrow
    const pos = e.target.getStage().getPointerPosition();
    new2DArrow.current.visible = true;
    new2DArrow.current.x1 = pos.x;
    new2DArrow.current.y1 = pos.y;
    new2DArrow.current.x2 = pos.x;
    new2DArrow.current.y2 = pos.y;
    update2DArrowRect();
  };

  const on2DMapMouseMoveHandler = (e) => {
    if (!isDrawing) return;
    const pos = e.target.getStage().getPointerPosition();
    new2DArrow.current.x2 = pos.x;
    new2DArrow.current.y2 = pos.y;
    update2DArrowRect();
  };

  const on2DMapMouseUpHandler = (e) => {
    if (!isDrawing) return;
    const pos = e.target.getStage().getPointerPosition();
    new2DArrow.current.x2 = pos.x;
    new2DArrow.current.y2 = pos.y;
    update2DArrowRect();

    console.log("setIsDrawing false");
    setIsDrawing(false);
    const eventId = editing;

    updateData(
      null,
      "X1",
      Number(eventId),
      null,
      (new2DArrow.current.x1 * fieldWidth) / 595
    );
    updateData(
      null,
      "Y1",
      Number(eventId),
      null,
      (new2DArrow.current.y1 * fieldHeight) / 386
    );
    updateData(
      null,
      "X2",
      Number(eventId),
      null,
      (new2DArrow.current.x2 * fieldWidth) / 595
    );
    updateData(
      null,
      "Y2",
      Number(eventId),
      null,
      (new2DArrow.current.y2 * fieldHeight) / 386
    );
  };

  const onMouseDownHandler = (e) => {
    if (!drawing) return;
    if (e.evt.button === 0 && e.evt.ctrlKey) return; // dragging with Ctrl+LMB
    if (e.evt.button === 2) return; // RMB

    setIsDrawing(true);
    // start drawing arrow
    const pos = e.target.getStage().getPointerPosition();
    newArrow.current.visible = true;
    newArrow.current.x1 = pos.x;
    newArrow.current.y1 = pos.y;
    newArrow.current.x2 = pos.x;
    newArrow.current.y2 = pos.y;
    new2DArrow.current.visible = false;
    updateArrowRect();
    update2DArrowRect();
  };

  const onMouseMoveHandler = (e) => {
    if (e.evt.button === 2) return;
    if (!newArrow.current.visible || !isDrawing) {
      return;
    }
    const pos = e.target.getStage().getPointerPosition();
    newArrow.current.x2 = pos.x;
    newArrow.current.y2 = pos.y;
    updateArrowRect();
  };

  const onMouseUpHandler = async (e) => {
    try {
      if (!isDrawing) {
        return;
      }
      // newArrow.current.visible = false;
      updateArrowRect();
      setIsDrawing(false);

      const eventId = editing;
      const [x1, y1] = await matchApi.get2Dfrom3D(videoId, [
        (newArrow.current.x1 - stageX) / stageScale / ratio,
        (newArrow.current.y1 - stageY) / stageScale / ratio - initialY,
      ]);
      const [x2, y2] = await matchApi.get2Dfrom3D(videoId, [
        (newArrow.current.x2 - stageX) / stageScale / ratio,
        (newArrow.current.y2 - stageY) / stageScale / ratio - initialY,
      ]);
      updateData(null, "X1", Number(eventId), null, x1);
      updateData(null, "Y1", Number(eventId), null, y1);
      updateData(null, "X2", Number(eventId), null, x2);
      updateData(null, "Y2", Number(eventId), null, y2);
    } catch (err) {}
  };

  const onRatioChange = (ratio) => {
    setRatio(ratio);
  };

  const onSetInitialY = (y) => {
    setInitialY(y);
  };

  const updateStartTime = () => {
    const eventId = editing;
    updateData(
      null,
      "Start Time",
      Number(eventId),
      null,
      frameToTime(Math.round(videoRef.current.currentTime * fps), fps)
    );
  };

  const updateEndTime = () => {
    const eventId = editing;
    updateData(
      null,
      "End Time",
      Number(eventId),
      null,
      frameToTime(Math.round(videoRef.current.currentTime * fps), fps)
    );
  };

  const showSubEventsList = (visibleSubEvents, onSelectEvent, eventType) => {
    const videoPlayerElement = document.querySelector(
      ".video-player-container"
    );
    let position = { x: 10, y: 10 };

    if (videoPlayerElement) {
      const rect = videoPlayerElement.getBoundingClientRect();
      position = {
        x: 10,
        y: rect.height / 2,
      };
    }

    setSubEventsListData({
      isOpen: true,
      visibleSubEvents,
      onSelectEvent,
      position,
      eventType,
    });
  };

  const closeSubEventsList = () => {
    if (editing) {
      // Reset any temporary sub-event changes
      const targetEvent = tableEvents.find(({ id }) => id === Number(editing));
      if (targetEvent) {
        const currentSubEvents = targetEvent.extra.additional_events || {};
        // Update the cell data with original values
        tableMeta?.updateData(
          null,
          "additional_events",
          Number(editing),
          null,
          currentSubEvents
        );
      }
    }
    setSubEventsListData((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  const tableMeta = {
    editingId: editing,
    setEditingEventId: setEditing,
    previewEventId,
    setPreviewEventId,
    deleteEvent,
    updateData,
    updateEvent,
    showSubEventsList,
    closeSubEventsList,
    setSubEventsListData,
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      // Handle 'S' key for start time
      if (e.key.toLowerCase() === "s") {
        setStart();
      }
      // Handle 'G' key for end time
      if (e.key.toLowerCase() === "g") {
        setEnd();
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    // Cleanup
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [editing, newEvent, isAdding]); // Dependencies include editedRows since we check it in the handler

  const setStart = () => {
    if (!editing) {
      setStartTime(videoRef.current.currentTime);
    } else {
      updateStartTime();
    }
  };

  const setEnd = () => {
    if (!editing) {
      setEndTime(videoRef.current.currentTime);
    } else {
      updateEndTime();
    }
  };

  useEffect(() => {
    if (editing) {
      const eventId = editing;
      const targetEvent = tableEvents.find(({ id }) => id === Number(eventId));
      setEditingEvent(targetEvent);
    } else {
      setEditing(false);
      setEditingEvent(null);
    }
  }, [editing]);

  return (
    <>
      {!isLoading ? (
        <Container className="py-3 event-validation-container" fluid>
          <div className="tw-flex tw-gap-1 tw-flex-col lg:tw-flex-row tw-z-10">
            <div className="tw-flex-1 tw-flex tw-relative">
              <VideoPlayer
                className="tw-w-full tw-relative"
                src={videoUrl}
                fps={fps}
                ref={videoRef}
                videoProps={{
                  autoPlay: false,
                  loop: false,
                }}
                frameStep={frameStep}
                startTime={videoStartTime}
                endTime={videoEndTime}
                tracks={supertracks}
                activeTracks={showAll && !previewEvent ? null : activeTracks}
                onFrameChange={onFrameChange}
                onVideoDomSizeChange={onVideoDomSizeChange}
                onMouseDownHandler={onMouseDownHandler}
                onMouseMoveHandler={onMouseMoveHandler}
                onMouseUpHandler={onMouseUpHandler}
                onStageScaleChange={onStageScaleChange}
                onStagePosChange={onStagePosChange}
                onRatioChange={onRatioChange}
                onSetInitialY={onSetInitialY}
              >
                <Arrow ref={newArrowRef} name="newArrow" />
              </VideoPlayer>

              <div
                className="tw-flex tw-justify-center tw-gap-2"
                style={{
                  position: "absolute",
                  top: `${videoDomHeight - 40}px`,
                  left: 0,
                  right: 0,
                  color: "white",
                }}
              >
                {realCurrentEvents.map(({ id, event_type }) => (
                  <span key={id}>{event_type}</span>
                ))}
              </div>
            </div>
            <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-2 tw-relative">
              <div className="tw-flex tw-justify-start tw-items-center">
                <Map
                  data={
                    isAdding
                      ? [newEvent]
                      : editingEvent
                      ? [editingEvent]
                      : previewEvent
                      ? [previewEvent]
                      : realCurrentEvents
                  }
                  fieldWidth={fieldWidth}
                  fieldHeight={fieldHeight}
                  onMouseDownHandler={on2DMapMouseDownHandler}
                  onMouseMoveHandler={on2DMapMouseMoveHandler}
                  onMouseUpHandler={on2DMapMouseUpHandler}
                >
                  <Arrow ref={new2DArrowRef} name="new2DArrow" />
                </Map>
              </div>
              <SubEventsList
                visibleSubEvents={subEventsListData.visibleSubEvents}
                onSelectEvent={subEventsListData.onSelectEvent}
                isOpen={subEventsListData.isOpen}
                position={subEventsListData.position}
              />
            </div>
          </div>
          <div
            className="d-flex flex-column tw-gap-1 events-showing-section tw-z-30 tw-relative"
            xs={12}
            xl={6}
          >
            <div className="tw-flex tw-justify-between tw-items-center">
              <div className="tw-flex tw-gap-2 tw-items-center">
                <Switch
                  className=""
                  label="Show All"
                  checked={showAll}
                  onChange={setShowAll}
                />
                <Button variant="success" size="sm" onClick={setStart}>
                  Start
                </Button>
                <Button variant="success" size="sm" onClick={setEnd}>
                  End
                </Button>
                <Button
                  className="add-new-event-btn"
                  variant="success"
                  size="sm"
                  onClick={addNewEvent}
                >
                  Add New Event
                </Button>
                {startTime !== null && <span>Start Time: {startTime}</span>}
                {endTime !== null && <span>End Time: {endTime}</span>}
              </div>
              <div className="tw-flex tw-gap-2 tw-justify-end">
                {editing ? (
                  <>
                    {drawing ? (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => {
                          setDrawing(false);
                          setIsDrawing(false);
                          newArrow.current.visible = false;
                          updateArrowRect();
                        }}
                      >
                        <FontAwesomeIcon icon={["fas", "pencil"]} /> Turn Off
                        Drawing
                      </Button>
                    ) : (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => {
                          setDrawing(true);
                        }}
                      >
                        <FontAwesomeIcon icon={["fas", "pencil"]} size="sm" />{" "}
                        Draw
                      </Button>
                    )}
                  </>
                ) : null}
                {previewEventId !== null && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={turnOffPreviewMode}
                  >
                    <FontAwesomeIcon icon={["fas", "eye-slash"]} /> Turn Off
                    Preview Mode
                  </Button>
                )}
                <Button
                  variant="success"
                  size="sm"
                  disabled={isSaving}
                  onClick={saveAnswer}
                >
                  {isSaving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />{" "}
                      Saving
                    </>
                  ) : (
                    "Save Answer"
                  )}
                </Button>
              </div>
            </div>
            <div
              className="event-list-table overflow-auto flex-grow-1 position-relative"
              ref={tableRef}
            >
              <EventsTable
                columns={columns}
                tableEvents={tableEvents}
                editingId={editing}
                setEditingEventId={setEditing}
                previewEventId={previewEventId}
                setPreviewEventId={setPreviewEventId}
                deleteEvent={deleteEvent}
                updateData={updateData}
                updateEvent={updateEvent}
                showSubEventsList={showSubEventsList}
                setSubEventsListData={setSubEventsListData}
              />
            </div>
          </div>
        </Container>
      ) : (
        <Preloader className="m-auto" />
      )}
    </>
  );
};

export default Validation;
